import type { QueryClient } from '@tanstack/react-query'
import createIri from '@/lib/helpers/createIri.ts'
import type { IssueTypeT } from '@/components/common/menu/lib/types/navigationDataTypes.ts'
import {
  isCreatableIssueType,
  readLastUsedIssueType,
  resolveGlobalIssueTypeIri,
} from '@/routes/projectStages/issues/lib/lastUsedIssueType.ts'

/**
 * Issue-type questions the quick-capture screen asks about an ARBITRARY type — not the route's
 * current type, which is all `useProjectStageStore`/`useGetCurrIssueTProps` can answer. Each
 * answer mirrors the New Issue form's own derivation and names where that lives.
 */

// The key `currStageIssueTypesQuery` (lib/actions/getStageIssuesTypes.ts) seeds from rootLoader.
const ISSUE_TYPES_QUERY_PREFIX = 'currStageIssueTypes'

type IdT = string | number

export function issueTypeIri(issueType: Pick<IssueTypeT, 'id'>): string {
  return createIri(`issue_types`, issueType.id)
}

/**
 * The stage's issue types as the stage loader last cached them. rootLoader keys the query with
 * the route param (a string) and other callers may use a number, so both spellings count. Empty
 * when nothing is cached — a loader cannot wait for the parent loader that fills it.
 */
export function cachedStageIssueTypes(client: QueryClient, projectStageId: IdT): IssueTypeT[] {
  const entries = client.getQueriesData<IssueTypeT[] | null>({
    queryKey: [ISSUE_TYPES_QUERY_PREFIX],
    predicate: query => {
      const params = query.queryKey[1] as { projectStageId?: IdT } | undefined
      return String(params?.projectStageId) === String(projectStageId)
    },
  })
  return entries.map(([, data]) => data).find(data => Array.isArray(data)) ?? []
}

/** The types the user may create on — the only ones the type selector offers. */
export function creatableIssueTypes(issueTypes: IssueTypeT[] | null | undefined): IssueTypeT[] {
  return (issueTypes ?? []).filter(isCreatableIssueType)
}

/** Last used type for this user and stage if still creatable, else the first creatable one. */
export function defaultQuickCaptureIssueTypeIri({
  issueTypes,
  userId,
  projectStageId,
}: {
  issueTypes: IssueTypeT[]
  userId: IdT | null | undefined
  projectStageId: IdT | null | undefined
}): string | null {
  return resolveGlobalIssueTypeIri({
    issueTypes,
    lastUsedIri: readLastUsedIssueType(userId, projectStageId),
    currIssueTypeIri: null,
  })
}

/**
 * The status a new issue of this type starts in: the first status of the type's status group.
 * Same derivation as the form — `createStatusGroupProperty` maps `statusGroup.statuses` to
 * `{ value: status['@id'] }` and `NewIssueDialog` defaults to `statuses[0].value`. Null when the
 * type has no statuses, in which case no status is sent at all.
 */
export function defaultStatusIri(issueType: IssueTypeT | null | undefined): string | null {
  return issueType?.statusGroup?.statuses?.[0]?.['@id'] ?? null
}

/** Whether the type has a recipient field at all (the form's `hasRecipient` check). */
export function typeHasRecipient(issueType: IssueTypeT | null | undefined): boolean {
  return issueType?.availableProperties?.includes('hasRecipient') === true
}

/** User IRIs this user may address an issue of this type to (the form picker's filter). */
export function addressableRecipientIris(issueType: IssueTypeT | null | undefined): string[] {
  return issueType?.currentUserCanAddressTo ?? []
}

/**
 * The recipient to send for this type, or null to send none.
 *
 * A recipient goes out only when the type has a recipient field AND the user may address this
 * person on this type. Anything else is dropped rather than sent: the server refuses a recipient
 * on a type without one (IsRecipientValidValidator), and a 4xx is permanent in the offline queue,
 * so a refused field would lose the whole capture. A published issue without a recipient is
 * valid (G0), so dropping is always the safe direction.
 */
export function recipientForType(
  issueType: IssueTypeT | null | undefined,
  recipientIri: string | null | undefined,
): string | null {
  if (!recipientIri || !typeHasRecipient(issueType)) return null
  return addressableRecipientIris(issueType).includes(recipientIri) ? recipientIri : null
}
